import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from './configuration.service';
import { LoginResult } from '../models/loginResult';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { UserPermission } from '../models/userPermission';
import { Player } from '../models/player';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  constructor(private configService:ConfigService, private http:HttpClient, private cookieService:CookieService) { 
    this.baseUrl = configService.getApiEndpoint();
    this.authenticationSubject = new BehaviorSubject<string>('');
    this.authenticationSubscription = this.authenticationSubject.asObservable();
  }

  cookieAuthKey:string = `${this.configService.environment}.swinglab.loggedinuser`;
  baseUrl:string;
  loginResult:LoginResult = { isSuccess: false, firstName: '', lastName: '', username: '' };
  authenticationSubscription:Observable<string>;
  authenticationSubject:BehaviorSubject<string>;
  private permissions:Array<string>|null = null;

  getLoggedInUsername():string {
    return this.loginResult.username;
  }

  isAdmin():boolean {
    let username = this.getLoggedInUsername();
    return username === "krummy";
  }

  isAuthenticated():boolean {
    if(this.loginResult.isSuccess)
      return true;
    else {
      let user  = this.getUserFromStorage();
      return user !== null;
    }
  }

  getUserFromStorage() {
    let playerInfo = this.cookieService.get(this.cookieAuthKey);
    if(!playerInfo)
      return null;

    this.loginResult = JSON.parse(playerInfo);
    return this.loginResult;
  }


  async login(username:string, password:string):Promise<boolean> {
    let endpoint = `${this.baseUrl}/login`;
    let args = { username: username, password: password };
    let result = await firstValueFrom(this.http.post<LoginResult>(endpoint, args));
    this.loginResult = result;

    this.cookieService.set(this.cookieAuthKey, JSON.stringify(result), this.configService.getAuthCookieExpireDays());
    this.publishLogin();
    return result.isSuccess;
  }

  async logout() {
    this.loginResult ={ isSuccess: false, firstName: '', lastName: '', username: '' };
    this.permissions = null;
    this.cookieService.delete(this.cookieAuthKey);
    this.publishLogout();
  }

  publishLogin():void {
    this.authenticationSubject.next("LOGIN");
  }

  publishLogout():void {
    this.authenticationSubject.next("LOGOUT");
  }

  async userHasPermission(permission:string):Promise<boolean> {
    if(!this.isAuthenticated()) return false;
    let result: boolean = false;

    if(!this.permissions) {
      //Null means we haven't checked the server. Empty array means we've checked, the user just has no records.
      this.permissions = [];
      let endpoint = `${this.baseUrl}/userpermissions`;
      let args = { username: this.loginResult.username };
      let permissionResult = await firstValueFrom(this.http.post<Array<UserPermission>>(endpoint, args));
      this.permissions = [...new Set(permissionResult.map(p => p.permission))];
    }

    if(this.permissions)
      result = this.permissions.includes(permission);

    return result;
  }

  async getAllPlayers():Promise<Array<Player>> {
    let endpoint = `${this.baseUrl}/players`;
    let playersResult = await firstValueFrom(this.http.post<Array<Player>>(endpoint, {}));
    return playersResult;
  }

  async getPermissionsForUser(username:string):Promise<Array<UserPermission>> {
    let endpoint = `${this.baseUrl}/userpermissions`;
    let args = { username: username };
    let permissionResult = await firstValueFrom(this.http.post<Array<UserPermission>>(endpoint, args));
    return permissionResult;
  }

  async addPermission(username:string, permission:string):Promise<number> {
    let endpoint = `${this.baseUrl}/userpermission`;
    let args = { username: username, permission: permission };
    let permissionResult = await firstValueFrom(this.http.post<number>(endpoint, args));
    return permissionResult;
  }

  async removePermission(id:number):Promise<void> {
    let endpoint = `${this.baseUrl}/userpermission`;
    let args = { id: id };
    await firstValueFrom(this.http.request('DELETE', endpoint, { body: args }));
  }
}
